<script setup lang="ts">
import { inject, onMounted, type PropType } from "vue";
import type { CompositionBackgroundCardShadow, UIColor } from "../../..";
import { CompositionBackgroundCardInjectionKey } from "../../helpers.js";

defineProps({
  /*
      Backface color
      values: see src/css/color.css
    */
  color: {
    type: String as PropType<UIColor>,
    default: "--color-white",
  },

  /*
      Should the card background display a stack of cards
    */
  stacked: {
    type: Boolean,
    default: false,
  },

  /*
      Should the card have a shadow
      values: small, medium, large
    */
  shadow: {
    type: String as PropType<CompositionBackgroundCardShadow>,
    default: "none",
  },

  /*
   * Should the card have rounded corners
   */
  rounded: {
    type: Boolean,
    default: false,
  },
});

const backgroundsPopulated = inject(CompositionBackgroundCardInjectionKey);

onMounted(() => {
  if (backgroundsPopulated) backgroundsPopulated.value = true;
});
</script>

<template>
  <div
    :class="[
      $style.container,
      'composition-background-card',
      rounded && $style.rounded,
      $style[`shadow-${shadow}`],
    ]"
    :style="{ 'background-color': `var(${color})` }"
  >
    <div
      v-if="stacked"
      :class="[$style.background, rounded && $style.rounded]"
    />
  </div>
</template>

<style module>
.container,
.background,
.background:before,
.background:after {
  display: flex;
  position: absolute;
  width: 100%;
  height: 100%;
  /* border-radius: var(--border-radius-medium); */
}

.container {
  transition-property: box-shadow;
  transition-duration: var(--transition-duration-short);
  transition-timing-function: ease-in-out;
}

.shadow-none {
  box-shadow: none;
}

.shadow-small {
  box-shadow: var(--box-shadow-small);
}

.shadow-medium {
  box-shadow: var(--box-shadow-medium);
}

.shadow-large {
  box-shadow: var(--box-shadow-large);
}

.container:not(.shadow-small):not(.shadow-medium):not(.shadow-large) {
  clip-path: inset(0 0 -5rem 0);
}

.rounded {
  border-radius: var(--border-radius-large);
}

.background,
.background:before,
.background:after {
  box-shadow: 0 0.2rem 0.75rem rgba(0, 0, 0, 0.1);
  background-color: inherit;
}

.background:before {
  content: "";
  width: calc(100% - 2rem);
  margin-top: 0.6rem;
  margin-left: 1rem;
  filter: brightness(0.98);
  z-index: -1;
}

.background:after {
  content: "";
  width: calc(100% - 4rem);
  margin-top: 1.2rem;
  margin-left: 2rem;
  filter: brightness(0.97);
  z-index: -2;
}
</style>
